import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

interface AuditEvent {
  id: string;
  accountId: string | null;
  action: string;
  resource: string;
  resourceId: string | null;
  status: string;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: any;
  createdAt: string;
  account: { email: string; displayName: string | null } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function HealthAuditLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '1');
  const action = searchParams.get('action') || '';
  const accountId = searchParams.get('accountId') || '';

  const fetchEvents = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', '50');
    if (action) params.set('action', action);
    if (accountId) params.set('accountId', accountId);

    fetch(`/api/admin/health/audit-log?${params}`)
      .then((r) => r.json())
      .then((data) => {
        setEvents(data.events || []);
        setPagination(data.pagination);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, action, accountId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetch('/api/admin/health/audit-log/actions')
      .then((r) => r.json())
      .then((data) => setActions(data.actions || []))
      .catch(() => {});
  }, []);

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const statusBadge = (status: string) => {
    const cls = status === 'SUCCESS'
      ? 'text-green-400 bg-green-400/10'
      : status === 'FAIL'
      ? 'text-red-400 bg-red-400/10'
      : 'text-yellow-400 bg-yellow-400/10';
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="text-gray-400 mt-1">
            Health platform activity trail
            {accountId && ' (filtered by account)'}
          </p>
        </div>
        {accountId && (
          <Link
            to="/health/audit"
            className="text-sm text-primary-400 hover:text-primary-300"
          >
            Clear filter
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={action}
            onChange={(e) => {
              const params = new URLSearchParams(searchParams);
              if (e.target.value) params.set('action', e.target.value);
              else params.delete('action');
              params.set('page', '1');
              setSearchParams(params);
            }}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
          >
            <option value="">All Actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Action</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Account</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Resource</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">
                      {new Date(event.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-white">{event.action}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {event.account ? (
                        <Link
                          to={`/health/accounts/${event.accountId}`}
                          className="text-primary-400 hover:text-primary-300"
                        >
                          {event.account.email}
                        </Link>
                      ) : (
                        <span className="text-gray-500">{event.accountId ? event.accountId.slice(0, 8) : '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {event.resource}
                      {event.resourceId && (
                        <span className="text-gray-500 ml-1">({event.resourceId.slice(0, 8)})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{statusBadge(event.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                      {event.ipAddress || '—'}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Shield className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No audit events found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} events)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                  className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
